import { useCallback } from 'react';
import {
  Colors,
  Flex,
  IconArrowDown,
  Typography,
} from '@banhala/ui-kit';
import type { HeadlineSelectableOneLineProps } from './types';
import { isNil, isNotNil } from '../../../../utils';
import {
  useBottomSheetRoute,
  useIsInFragmentScreens,
  useOpenDeeplink,
} from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import { ViewAllButton } from '../ViewAllButton';
import {
  useFilterSelectBottomSheet,
  type FilterSelectBottomSheetParams,
  type FilterSelectBottomSheetResult,
} from './component/FilterSelectBottomSheet';

export const HeadlineSelectableOneLine = ({
  header,
  screen,
  handleFilterSelect,
}: HeadlineSelectableOneLineProps) => {
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const { show: showFilterSelectBottomSheet } = useFilterSelectBottomSheet();
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();

  const handlePressMore = useCallback(() => {
    if (isNil(header) || isNil(header.item.deeplink)) return;
    logAnalyticsEvent({
      name: 'CLICK_HEADLINE_SELECTABLE_ONE_LINE_MORE',
      analytics: {
        ...header.logging.analytics,
        ...(screen != null && { SCREEN: screen }),
      },
      ably_only_log: header.logging.ably_only_log,
    });
    openDeeplink(header.item.deeplink);
  }, [header, screen, openDeeplink, logAnalyticsEvent]);

  const handlePressFilter = useCallback(async () => {
    if (isNil(header)) return;
    logAnalyticsEvent({
      name: 'CLICK_HEADLINE_SELECTABLE_ONE_LINE_FILTER',
      analytics: {
        ...header.logging.analytics,
        ...(screen != null && { SCREEN: screen }),
      },
      ably_only_log: header.logging.ably_only_log,
    });

    const params: FilterSelectBottomSheetParams = {
      title: header.item.title,
      itemList:
        header.item.filter_list?.map(item => ({
          title: item.title,
          list: item.item_list.map(item => ({
            queryName: item.query_name,
            queryValue: item.query_value,
            name: item.name,
          })),
        })) ?? [],
      selected: header.item.selected_value
        ? {
            queryName: header.item.selected_value.query_name,
            queryValue: header.item.selected_value.query_value,
            name: header.item.selected_value.name,
          }
        : null,
    };

    const result = isInFragmentScreens
      ? await openBottomSheetRoute<
          FilterSelectBottomSheetParams,
          FilterSelectBottomSheetResult
        >('filter-select', params)
      : await showFilterSelectBottomSheet(params);

    if (result.type !== 'confirm') return;
    const { selected } = result.data;
    handleFilterSelect?.(selected.queryName, selected.queryValue);
  }, [
    handleFilterSelect,
    header,
    isInFragmentScreens,
    logAnalyticsEvent,
    openBottomSheetRoute,
    screen,
    showFilterSelectBottomSheet,
  ]);

  if (isNil(header)) return null;

  const paddingTop = header.item.padding_top ?? 24;
  const paddingBottom = header.item.padding_bottom ?? 16;
  const hasDeeplink = isNotNil(header.item.deeplink);

  return (
    <Flex.Row
      padding={{
        top: paddingTop,
        bottom: paddingBottom,
        left: 16,
        right: hasDeeplink ? 0 : 16,
      }}
      alignItems="center"
    >
      <Flex.Row flex={1} alignItems="center" gap={4}>
        {isNotNil(header.item.selected_value) && (
          <Flex.Row alignItems="center" gap={2} onPress={handlePressFilter}>
            <Typography type="h5" color="content_pink_primary">
              {header.item.selected_value.name}
            </Typography>
            <Flex.Center
              width={20}
              height={20}
              backgroundColor={'pink10'}
              borderRadius={10}
            >
              <IconArrowDown size={16} fill={Colors.content_pink_primary} />
            </Flex.Center>
          </Flex.Row>
        )}
        {isNotNil(header.item.postfix) && (
          <Typography type="h5" color="content_primary">
            {header.item.postfix}
          </Typography>
        )}
      </Flex.Row>

      {hasDeeplink && (
        <Flex padding={{ right: 4 }}>
          <ViewAllButton onPress={handlePressMore} />
        </Flex>
      )}
    </Flex.Row>
  );
};
